// utils/pbBlogTheme.js
// ---------------------------------------------------------------------------
// ProducerBuddy blog theme — cinematic, dark-first. Each post gets a unique
// deterministic accent tuned to glow against a deep charcoal base. Reuses the
// same embed/reading-time helpers but with a film-production visual identity.
// ---------------------------------------------------------------------------

// Brand constants
export const PB = {
  bg:        "#0a0a0f",   // deep cinema black
  bgRaised:  "#13131c",   // raised surface (cards)
  bgInput:   "#1a1a26",
  border:    "#262633",
  text:      "#f4f4f8",
  textDim:   "#9b9bac",
  gold:      "#f5c451",   // film gold — primary brand accent
  violet:    "#8b5cf6",   // electric violet — secondary
};

// Per-post accent palettes — each is a glow color + matching gradient that
// reads as premium on a near-black background.
const PB_PALETTES = [
  { name: "gold",    accent: "#f5c451", glow: "rgba(245,196,81,0.35)",  grad: "linear-gradient(135deg,#f5c451,#e08e3c)" },
  { name: "violet",  accent: "#8b5cf6", glow: "rgba(139,92,246,0.35)",  grad: "linear-gradient(135deg,#8b5cf6,#6366f1)" },
  { name: "ember",   accent: "#ff6b5d", glow: "rgba(255,107,93,0.32)",  grad: "linear-gradient(135deg,#ff6b5d,#ff3d7f)" },
  { name: "teal",    accent: "#2dd4bf", glow: "rgba(45,212,191,0.30)",  grad: "linear-gradient(135deg,#2dd4bf,#0ea5e9)" },
  { name: "rose",    accent: "#fb7185", glow: "rgba(251,113,133,0.32)", grad: "linear-gradient(135deg,#fb7185,#e11d48)" },
  { name: "azure",   accent: "#60a5fa", glow: "rgba(96,165,250,0.32)",  grad: "linear-gradient(135deg,#60a5fa,#3b82f6)" },
  { name: "lime",    accent: "#a3e635", glow: "rgba(163,230,53,0.28)",  grad: "linear-gradient(135deg,#a3e635,#22c55e)" },
  { name: "magenta", accent: "#e879f9", glow: "rgba(232,121,249,0.32)", grad: "linear-gradient(135deg,#e879f9,#c026d3)" },
];

export function getPBPalette(id = "") {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return PB_PALETTES[Math.abs(hash) % PB_PALETTES.length];
}

export function getEmbedInfo(url = "") {
  if (!url) return null;

  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)/);
  if (yt) {
    const isShort = /\/shorts\//.test(url);
    return { type: "youtube", embedUrl: `https://www.youtube.com/embed/${yt[1]}`, aspect: isShort ? "9/16" : "16/9" };
  }
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { type: "vimeo", embedUrl: `https://player.vimeo.com/video/${vimeo[1]}`, aspect: "16/9" };

  const insta = url.match(/instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/);
  if (insta) return { type: "instagram", embedUrl: `https://www.instagram.com/p/${insta[1]}/embed`, aspect: "4/5" };

  const tiktok = url.match(/tiktok\.com\/.*\/video\/(\d+)/);
  if (tiktok) return { type: "tiktok", embedUrl: `https://www.tiktok.com/embed/v2/${tiktok[1]}`, aspect: "9/16" };

  return { type: "link", embedUrl: url, aspect: null };
}

export function getReadingTime(html = "") {
  const text = html.replace(/<[^>]+>/g, " ").trim();
  const words = text ? text.split(/\s+/).length : 0;
  return Math.max(1, Math.round(words / 200));
}

export function stripHtml(html = "") {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}